import { ModuleLayout } from '@billy/design-system'
import { BetaBadge, CashPilot, setReferenceDate } from '@shinetools/cashpilot'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useLocalStorage } from 'react-use'

import { createCashPilotNavigate } from '@features/cashPilot/createCashPilotNavigate'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../hooks/useSegment'
import { getAccessToken } from '../../utils'

type RenderedViewProps = {
  sideActions: ReactElement
  content: ReactElement
}

// This separate RenderedView component is needed so Cash Pilot can be
// rendered with all the appropriate contexts and providers (translation, queries, etc).
const RenderedView = ({ content, sideActions }: RenderedViewProps) => {
  const { t } = useTranslation()

  return (
    // The cash pilot view needs a ModuleLayout wrapper
    // to allow a proper vertical scroll with SimpleBar
    <ModuleLayout
      fullWidth
      fullHeight
      title={t('cashpilot.header.title')}
      headerContent={<BetaBadge />}
      sideActions={sideActions}
    >
      {content}
    </ModuleLayout>
  )
}

export const CashPilotView = () => {
  const { i18n } = useTranslation()
  const { organization } = useUserOrganization()
  const accessToken = getAccessToken()
  const [referenceDateStr] = useLocalStorage<string>('labs-flag:cashpilotReferenceDate', undefined, { raw: true })
  const referenceDate = useMemo(() => (referenceDateStr ? new Date(referenceDateStr) : undefined), [referenceDateStr])
  const navigate = useNavigate()
  const { track } = useSegment()

  setReferenceDate(referenceDate)

  const handleNavigate = useMemo(
    () => createCashPilotNavigate(organization?.url, navigate),
    [navigate, organization?.url],
  )

  if (!organization || !accessToken) {
    return null
  }

  return (
    <CashPilot
      accessToken={accessToken}
      language={i18n.language}
      organizationId={organization.id}
      apiUrl={window.ENV.newApiUrl}
      render={({ sideActions, content }) => <RenderedView sideActions={sideActions} content={content} />}
      // Cash Pilot's theme is stuck on light as long as Billy uses a filter-based dark mode
      theme="light"
      navigate={handleNavigate}
      trackEvent={track}
    />
  )
}
